import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, isValidEmail } from "@/lib/api";
import { MailCheck } from "lucide-react";
import Navbar from "@/components/Navbar";


export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isValidEmail(email)) { setError("Enter a valid email address."); return; }
    setBusy(true);
    try {
      // Enumeration-safe: the server returns the same thing regardless of existence.
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch {
      // Even on an unexpected error, don't reveal anything — show the same state.
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-foreground" data-testid="forgot-page">
      <Navbar />
      <div className="flex items-center justify-center px-6 py-10 min-h-[calc(100vh-4rem)]">
      <div className="w-full max-w-sm animate-fade-up">

        {sent ? (
          <div data-testid="forgot-confirmation" className="border border-olive/40 bg-olive/5 p-5">
            <MailCheck className="w-7 h-7 text-olive-bright" />
            <p className="font-heading text-2xl font-bold text-foreground mt-3">Check your inbox.</p>
            <p className="text-sm text-secondary-foreground mt-2 font-body leading-relaxed">
              If an account exists for that address, we've sent a link to set a new password. It works once and expires in 30 minutes.
            </p>
            <p className="text-xs text-muted-foreground mt-3 font-body leading-relaxed">
              This response is identical whether or not an account exists, so this page can't be used to find out who's registered.
            </p>
            <Link to="/login" data-testid="forgot-back-login" className="mt-4 inline-flex w-full items-center justify-center bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-sm py-2.5 transition-colors">
              Back to Log In
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Reset Password</h1>
            <p className="text-sm text-secondary-foreground mt-1 font-body">
              Enter your email and we'll send a link to set a new password. This also works if you signed up with Google and never set one.
            </p>
            <form onSubmit={submit} className="space-y-4 mt-6">
              {error && <div data-testid="forgot-error" className="border border-blaze/50 bg-blaze/10 text-blaze text-sm px-3 py-2 font-mono">{error}</div>}
              <div>
                <label htmlFor="fp-email" className="font-mono text-xs uppercase tracking-wide text-secondary-foreground">Email</label>
                <input id="fp-email" data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground transition-colors" />
              </div>
              <button data-testid="forgot-submit" disabled={busy}
                className="w-full bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-base py-3 transition-colors">
                {busy ? "Sending..." : "Send Reset Link"}
              </button>
            </form>
            <p className="mt-6 text-sm text-secondary-foreground font-body">
              Remembered it? <Link to="/login" className="text-olive-bright hover:text-volt">Log in</Link>
            </p>
          </>
        )}
      </div>
      </div>
    </div>
  );
}
